import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AgentClientService, AppTheme } from '../client/agent-client';
import { StorageService } from '../shared/storage/storage.service';
import { PoliticsModeService } from './politics-mode.service';

/** Tema aplicado da última vez; sobrevive ao fechamento do app de propósito. */
const APP_THEME_KEY = 'rsm.app.theme';

/** Azul histórico — o mesmo que está em variables.scss. */
const DEFAULT_THEME: AppTheme = {
  color: 'BLUE',
  hex: '#208bff',
  shade: '#1c7ae0',
  tint: '#3697ff',
  contrast: '#ffffff',
  deep: '#003997',
};

/**
 * Cor base do aplicativo, definida pelo cliente no painel.
 *
 * <p>A cor customizada vale SOMENTE no ambiente RSM Politics. No comercial o app
 * volta ao azul padrão: ali a marca é a RSM, não a do cliente. Por isso o serviço
 * acompanha o modo e repinta a cada troca de ambiente.</p>
 *
 * <p>Aplica sobre os tokens `--rsm-brand*` de `variables.scss`, dos quais toda a
 * interface deriva. Trocar esses tokens repinta o app inteiro — barra inferior,
 * botões, Carteira, Placar — sem que nenhuma tela precise saber que existe
 * customização de cor.</p>
 *
 * <p>A cor da empresa é lida do armazenamento ANTES da rede. Sem isso, entrar no
 * Politics mostraria o azul e pularia para a cor do cliente quando a resposta
 * chegasse, a cada abertura do app.</p>
 */
@Injectable({ providedIn: 'root' })
export class AppThemeService {

  /** Cor da empresa do agente. Só chega à tela dentro do ambiente Politics. */
  private companyTheme: AppTheme = DEFAULT_THEME;

  /** O que está pintado agora — muda junto com o ambiente. */
  private current: AppTheme = DEFAULT_THEME;

  constructor(
    private agentClientService: AgentClientService,
    private storageService: StorageService,
    private politicsModeService: PoliticsModeService,
  ) {
    // O ambiente manda na cor: sair do Politics tem que devolver o azul da RSM
    // na hora, sem esperar recarregar nada.
    this.politicsModeService.mode$.subscribe((mode) => this.applyForMode(mode));
  }

  get theme(): AppTheme {
    return this.current;
  }

  /**
   * Aplica o tema guardado localmente. Chamado na inicialização, antes de
   * qualquer requisição — é o que evita o piscar de cor na abertura.
   */
  async applyStoredTheme(): Promise<void> {
    try {
      // StorageService.get devolve `this.storage?.get(...)`: enquanto o Ionic
      // Storage não terminou o init o retorno é undefined, e encadear .then()
      // aqui estoura TypeError. Promise.resolve normaliza os dois casos.
      const stored = await Promise.resolve(this.storageService.get(APP_THEME_KEY));
      if (stored?.hex) {
        // Guarda a cor, mas NÃO pinta direto: quem decide é o ambiente. Pintar
        // aqui deixaria o comercial com a cor do cliente até o modo ser
        // restaurado logo em seguida.
        this.companyTheme = stored as AppTheme;
        this.applyForMode(this.politicsModeService.isPolitics ? 'POLITICS' : 'COMMERCIAL');
      }
    } catch {
      // Sem tema guardado o app segue no azul padrão, que é o comportamento
      // de sempre. Cor é acabamento: não pode impedir a abertura.
    }
  }

  /**
   * Busca a cor da empresa e aplica. Chamado no login e na troca de contexto,
   * que é quando a empresa do agente pode ter mudado.
   */
  refresh(): Observable<AppTheme> {
    return this.agentClientService.getAppTheme().pipe(
      tap((theme) => {
        if (theme?.hex) {
          this.companyTheme = theme;
          this.storageService.set(APP_THEME_KEY, theme);
          this.applyForMode(this.politicsModeService.isPolitics ? 'POLITICS' : 'COMMERCIAL');
        }
      }),
      catchError(() => of(this.current)),
    );
  }

  /** Volta ao azul padrão e esquece o tema — usado no logout. */
  reset(): void {
    this.companyTheme = DEFAULT_THEME;
    this.apply(DEFAULT_THEME);
    this.storageService.remove(APP_THEME_KEY);
  }

  /**
   * No Politics vale a cor do cliente; no comercial, o azul da RSM.
   *
   * <p>A marca do cliente aparece no ambiente do cliente. O ambiente comercial é
   * da RSM e continua com a identidade dela, mesmo para o agente que apoia uma
   * base Politics.</p>
   */
  private applyForMode(mode: 'COMMERCIAL' | 'POLITICS'): void {
    this.apply(mode === 'POLITICS' ? this.companyTheme : DEFAULT_THEME);
  }

  private apply(theme: AppTheme): void {
    this.current = theme;

    const root = document.documentElement;
    root.style.setProperty('--rsm-brand', theme.hex);
    root.style.setProperty('--rsm-brand-rgb', this.toRgbChannels(theme.hex));
    root.style.setProperty('--rsm-brand-shade', theme.shade || theme.hex);
    root.style.setProperty('--rsm-brand-tint', theme.tint || theme.hex);
    root.style.setProperty('--rsm-brand-contrast', theme.contrast || '#ffffff');

    // O tom escuro da marca é o shade: é ele que a barra inferior e os botões de
    // ação usavam como azul fixo.
    root.style.setProperty('--rsm-brand-dark', theme.shade || theme.hex);
    root.style.setProperty('--rsm-brand-dark-rgb', this.toRgbChannels(theme.shade || theme.hex));

    // O tom profundo NAO é o shade: no azul ele é #003997, bem mais escuro que
    // o #1c7ae0 de estado pressionado. Vem pronto do servidor por isso.
    const deep = theme.deep || theme.shade || theme.hex;
    root.style.setProperty('--rsm-color-primary-dark', deep);
    root.style.setProperty('--rsm-color-primary-dark-rgb', this.toRgbChannels(deep));
  }

  /**
   * `#208bff` vira `32, 139, 255`. `rgba()` não aceita hexadecimal vindo de
   * var(), então os canais precisam existir como token separado.
   */
  private toRgbChannels(hex: string): string {
    const normalized = `${hex || ''}`.trim().replace('#', '');
    if (normalized.length !== 6) {
      return '32, 139, 255';
    }

    const r = parseInt(normalized.substring(0, 2), 16);
    const g = parseInt(normalized.substring(2, 4), 16);
    const b = parseInt(normalized.substring(4, 6), 16);

    if ([r, g, b].some((channel) => Number.isNaN(channel))) {
      return '32, 139, 255';
    }
    return `${r}, ${g}, ${b}`;
  }
}
